import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

/**
 * StatCard — single stat item in stats grid
 * Props:
 *   icon       ReactNode   — icon to display (lucide or any element)
 *   value      string|number
 *   label      string
 *   ariaLabel  string      — overrides default aria-label
 *   testId     string
 *   trend      'up'|'down'|'neutral'|null  — optional trend indicator
 *   trendLabel string      — accessible label for trend (e.g. "+3 bu hafta")
 *   accent     'primary'|'emerald'|'amber'|'rose'  — icon accent color (default: primary)
 */
export default function StatCard({ icon, value, label, ariaLabel, testId, trend, trendLabel, accent = 'primary' }) {
  const accentClass = {
    primary: 'text-[var(--brand-primary)]',
    emerald: 'text-emerald-500',
    amber:   'text-amber-500',
    rose:    'text-rose-500',
  }[accent] || 'text-[var(--brand-primary)]';

  const iconBgClass = {
    primary: 'bg-[color-mix(in_srgb,var(--brand-primary)_10%,white)] border-[color-mix(in_srgb,var(--brand-primary)_25%,white)]',
    emerald: 'bg-emerald-50 border-emerald-200',
    amber:   'bg-amber-50 border-amber-200',
    rose:    'bg-rose-50 border-rose-200',
  }[accent] || 'bg-blue-50 border-blue-100';

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus;
  const trendColor = trend === 'up' ? 'text-emerald-500' : trend === 'down' ? 'text-rose-500' : 'text-gray-400';

  return (
    <div
      className="bg-white rounded-2xl p-4 flex flex-col items-center gap-2 border-2 border-gray-200 shadow-[2px_2px_0px_0px_rgba(0,0,0,0.08)] min-h-[100px]"
      role="group"
      aria-label={ariaLabel || `${label}: ${value}`}
      data-testid={testId}
    >
      <div className={`w-9 h-9 rounded-xl border-2 flex items-center justify-center ${iconBgClass}`}>
        <span className={accentClass}>{icon}</span>
      </div>
      <span className="text-2xl font-black text-[var(--text-main)] tabular-nums leading-tight">
        {value}
      </span>
      <span className="text-xs font-medium text-[var(--text-muted)] text-center leading-tight">
        {label}
      </span>
      {trend && trendLabel && (
        <span
          className={`flex items-center gap-0.5 text-xs font-semibold ${trendColor}`}
          aria-label={trendLabel}
        >
          <TrendIcon size={11} aria-hidden="true" />
          {trendLabel}
        </span>
      )}
    </div>
  );
}
